import React, { useMemo, useState } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import {
  Heading,
  SecondaryButton,
  Text,
  Table,
  TableColumnConfig,
} from '@weave/design-system';
import { Org } from '@weave/schema-gen-ts/dist/apis/api/identity/v1/api.pb';
import { SsoStatus } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { Page, CenteredSpinningLoader } from '../shared';
import { useGetSsoDetailsForTenant } from '../../apis/sso/queries';
import { useQueries, UseQueryResult } from 'react-query';
import { getOrg } from '../../apis/identity-search/api';
import { IdentitySearchQueryKeys } from '../../apis/identity-search/queries';
import { Link } from 'react-router-dom';
import { LocationSearchContainer } from '../location/location-search-container/location-search.container';
import { LocationSearchModel } from '../../models/location-search.model';

const emptyState = css`
  padding: ${theme.spacing(1, 0)};
`;

const statCard = css`
  border: 1px solid ${theme.colors.gray300};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(3)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.gray100};
`;

const statNumber = css`
  font-size: 48px;
  font-weight: bold;
  color: ${theme.colors.weaveBlue};
  margin: 0;
`;

const statLabel = css`
  font-size: ${theme.fontSize(16)};
  color: ${theme.colors.gray600};
  margin-top: ${theme.spacing(1)};
`;

type SsoStatsTableRow = { tenant: string; orgId: string; orgName: string };

const columns: TableColumnConfig<SsoStatsTableRow>[] = [
  {
    Header: 'Organization Name',
    id: 'orgName',
    accessor: (row) => (
      <Link
        to={`/org/${row.orgId}`}
        css={css`
          text-decoration: none;
          color: ${theme.colors.weaveBlue};
          font-weight: 600;
        `}
      >
        {row.orgName}
      </Link>
    ),
  },
  {
    Header: 'Organization ID',
    id: 'orgId',
    accessor: (row) => row.orgId,
  },
  {
    Header: 'Auth Tenant',
    id: 'tenant',
    accessor: (row) => row.tenant,
  },
];

export const SsoStatsPage = () => {
  const [selectedLocation, setSelectedLocation] = useState<LocationSearchModel>();
  const {
    data: ssoData,
    isLoading: isSsoLoading,
    error: ssoError,
  } = useGetSsoDetailsForTenant(selectedLocation?.LocationID ?? '', {
    select: (data) => ({
      ...data,
      orgIds:
        data.details?.connectionStatus === SsoStatus.SSO_ACTIVE
          ? [...new Set(data.details.orgIds ?? [])]
          : [],
    }),
  });
  const details = ssoData?.details;
  const orgIds = ssoData?.orgIds ?? [];

  // Fetch org details for all unique org IDs
  const orgQueries = useQueries(
    orgIds.map((id) => ({
      queryKey: [IdentitySearchQueryKeys.GET_ORG, id],
      queryFn: () => getOrg(id),
      select: (org) => (org as Org).name ?? 'Unknown Organization',
      staleTime: 5 * 60 * 1000,
    }))
  ) as UseQueryResult<string>[];

  // Build table data
  const tableRows = useMemo(() => {
    const rows: SsoStatsTableRow[] = orgQueries.map((query, index) => ({
      tenant: details?.tenant ?? '',
      orgId: orgIds[index],
      orgName: query.data ?? (query.isError ? 'Name unavailable' : 'Loading...'),
    }));
    rows.sort((a, b) => a.orgName.localeCompare(b.orgName));
    return rows;
  }, [details?.tenant, orgQueries, orgIds]);

  return (
    <Page title="SSO Statistics">
      <div
        css={css`
          margin-bottom: ${theme.spacing(3)};
          max-width: 560px;
        `}
      >
        {selectedLocation ? (
          <div>
            <Text>
              Location: {selectedLocation.Name} ({selectedLocation.LocationID})
            </Text>
            <SecondaryButton size="tiny" onClick={() => setSelectedLocation(undefined)}>
              Change location
            </SecondaryButton>
          </div>
        ) : (
          <LocationSearchContainer restrictToLocations onSelect={setSelectedLocation} />
        )}
      </div>
      {!selectedLocation ? (
        <Text color="light">
          Select a location to view organizations with active SSO in its tenant. This is
          not an all-WAM total.
        </Text>
      ) : isSsoLoading ? (
        <CenteredSpinningLoader
          css={css`
            min-height: 50vh;
          `}
        />
      ) : ssoError ? (
        <Text color="error">Failed to load SSO statistics.</Text>
      ) : !details ? (
        <Text color="light">No SSO tenant details are available for this location.</Text>
      ) : (
        <>
          <div css={statCard}>
            <p css={statNumber}>{orgIds.length}</p>
            <p css={statLabel}>Organizations with active SSO in this tenant</p>
          </div>

          <Heading level={2} css={{ marginBottom: theme.spacing(2) }}>
            Organizations in {details?.tenant ?? 'this tenant'}
          </Heading>

          {tableRows.length === 0 ? (
            <Text css={emptyState} color="light">
              No organizations with active SSO were found for this tenant.
            </Text>
          ) : (
            <Table colConfig={columns} data={tableRows} />
          )}
        </>
      )}
    </Page>
  );
};
